import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useEffect, useState } from 'react';
import { Box, Button, IconButton, Paper, Stack, Typography } from '@mui/material';
import { livyJson } from 'src/lib/livyApi';
import { AUTH_TOKEN_KEYS } from 'src/constants/authToken';
import { parseJwt } from 'src/core/services/jwt';
import { favoriteReportPrompt } from './pageHelp';

/** Opens the drawer, optionally asking a question. Any page can raise it; the launcher answers. */
export const ASK_EVENT = 'livy:ask';
export const askLivy = text => window.dispatchEvent(new CustomEvent(ASK_EVENT, {
  detail: text !== null && text !== void 0 ? text : ''
}));

/** Opens the drawer on its Help tab, searching for a topic. */
export const HELP_EVENT = 'livy:help';
/** What the Help tab is asked for: a topic to search, or a topic plus the article to open. */

export const helpLivy = (topic, article) => window.dispatchEvent(new CustomEvent(HELP_EVENT, {
  detail: article ? {
    topic,
    article
  } : topic
}));
/** One of the proxy's allowlisted clicks (engagement.mjs eventUpdate). Fire and forget: learning never blocks the UI. */
/** Fire and forget; the promise settles once the event is stored, for a caller that must refresh after it. */
export const livyEvent = (type, key) => livyJson('/engagement/event', {
  method: 'POST',
  body: {
    type,
    key
  }
}).then(() => undefined).catch(() => undefined);
const monthName = ym => new Date("".concat(ym, "-01T00:00:00Z")).toLocaleString('en-US', {
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC'
});

/** "Run my utility allocation export again for January 2026 to August 2026", from the person's own last range. */
export function resumePrompt(usualRange) {
  if (!usualRange) return null;
  const fromMonth = usualRange.fromMonth,
    toMonth = usualRange.toMonth;
  return fromMonth === toMonth ? "Run my utility allocation export again for ".concat(monthName(toMonth)) : "Run my utility allocation export again for ".concat(monthName(fromMonth), " to ").concat(monthName(toMonth));
}
export const loginKey = () => {
  var _localStorage$getItem, _claims$auth_time;
  const claims = parseJwt((_localStorage$getItem = localStorage.getItem(AUTH_TOKEN_KEYS.accessToken)) !== null && _localStorage$getItem !== void 0 ? _localStorage$getItem : '');
  return claims !== null && claims !== void 0 && claims.sub ? "".concat(claims.sub, ":").concat((_claims$auth_time = claims.auth_time) !== null && _claims$auth_time !== void 0 ? _claims$auth_time : '') : null;
};
let cached = null;
const listeners = new Set();

/** Drops the cached engagement and tells every mounted reader to fetch again (a star, a forget). */
export const resetEngagement = () => {
  cached = null;
  listeners.forEach(listener => listener());
};

/** Asked once per login: the proxy counts logins by auth_time, so a reload must not ask again. */
export function useEngagement(enabled) {
  const key = loginKey();
  const _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    state = _useState2[0],
    setState = _useState2[1];
  const _useState3 = useState(0),
    _useState4 = _slicedToArray(_useState3, 2),
    version = _useState4[0],
    setVersion = _useState4[1];
  useEffect(() => {
    const listener = () => setVersion(v => v + 1);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);
  useEffect(() => {
    var _cached;
    if (!enabled || !key) return undefined;
    if (((_cached = cached) === null || _cached === void 0 ? void 0 : _cached.key) !== key) {
      cached = {
        key,
        value: livyJson('/engagement').catch(() => ({
          enabled: false
        }))
      };
    }
    let live = true;
    void cached.value.then(value => live && setState({
      key,
      value
    }));
    return () => {
      live = false;
    };
  }, [enabled, key, version]);
  // A value fetched for the previous login must not greet the next one.
  return (state === null || state === void 0 ? void 0 : state.key) === key ? state.value : null;
}
const seenKey = () => "livy.greeted.".concat(loginKey());
export const greetingSeen = () => {
  try {
    return sessionStorage.getItem(seenKey()) === '1';
  } catch (_unused) {
    return false;
  }
};
export const markGreetingSeen = () => {
  try {
    sessionStorage.setItem(seenKey(), '1');
  } catch (_unused2) {
    /* a blocked store only means the greeting can show again this login */
  }
};

/** Three things worth asking today, by billing setup; setup comes first while a property is unfinished. */
export function greetingActions(billingType, inSetup, favorite) {
  const byType = {
    AppfolioIntegrated: 'Did this cycle’s charges reach AppFolio?',
    BilledThroughLivable: 'How much did we recover last month?',
    Convergent: 'What will my PMS bill residents this cycle?',
    CableInternet: 'How much internet did we recover last month?'
  };
  const audit = 'Run my utility allocation export for this year';
  // Someone who keeps running a different report gets it offered ahead of the audit; setup stays first.
  const mine = favorite === 'get_allocation_audit' ? undefined : favoriteReportPrompt(billingType, favorite);
  return [inSetup ? 'What should I do next to finish setup?' : 'What’s blocking this month’s billing?', mine !== null && mine !== void 0 ? mine : audit, mine ? audit : byType[billingType !== null && billingType !== void 0 ? billingType : 'BilledThroughLivable']];
}
/**
 * The login greeting: a small card beside the launcher, not a dialog. It never takes focus (a
 * polite status region announces it) and Esc or the close button puts it away for this login.
 */
export function LivyGreeting(_ref) {
  let actions = _ref.actions,
    resume = _ref.resume,
    onAsk = _ref.onAsk,
    onDismiss = _ref.onDismiss;
  useEffect(() => {
    const onKey = e => e.key === 'Escape' && onDismiss();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onDismiss]);
  return /*#__PURE__*/React.createElement(Paper, {
    role: "status",
    "aria-live": "polite",
    elevation: 6,
    sx: {
      position: 'fixed',
      // Clear of HubSpot's launcher; on a phone the full-width card sits above it instead.
      right: {
        xs: 16,
        sm: 112
      },
      bottom: {
        xs: 104,
        sm: 84
      },
      zIndex: theme => theme.zIndex.drawer - 1,
      width: 'min(320px, calc(100vw - 32px))',
      p: '14px 14px 12px',
      borderRadius: '14px'
    }
  }, /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    alignItems: "flex-start",
    spacing: "8px"
  }, /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 14,
      fontWeight: 600,
      flex: 1,
      lineHeight: 1.4
    }
  }, "Welcome, I'm here. Here's what I can help with today."), /*#__PURE__*/React.createElement(IconButton, {
    size: "small",
    "aria-label": "Dismiss Livy's greeting",
    onClick: onDismiss,
    sx: {
      mt: '-4px'
    }
  }, "\xD7")), /*#__PURE__*/React.createElement(Stack, {
    spacing: "6px",
    sx: {
      mt: '10px'
    }
  }, actions.map(a => /*#__PURE__*/React.createElement(Button, {
    key: a,
    size: "small",
    variant: "outlined",
    onClick: () => onAsk(a),
    sx: {
      justifyContent: 'flex-start',
      textAlign: 'left',
      textTransform: 'none',
      borderRadius: '10px',
      fontSize: 13
    }
  }, a))), resume && /*#__PURE__*/React.createElement(Box, {
    sx: {
      mt: '10px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 12,
      fontWeight: 600,
      color: 'text.secondary',
      mb: '4px'
    }
  }, "Pick up where you left off"), /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "outlined",
    onClick: () => onAsk(resume),
    sx: {
      justifyContent: 'flex-start',
      textAlign: 'left',
      textTransform: 'none',
      borderRadius: '10px',
      fontSize: 13,
      width: '100%'
    }
  }, resume)), /*#__PURE__*/React.createElement(Box, {
    sx: {
      mt: '8px',
      fontSize: 11.5,
      color: 'text.secondary'
    }
  }, "Livy is an AI assistant and can be wrong."));
}